// 権限マップの表示用ロジック（純関数）。描画から分離してテスト可能にする。
import { DANGEROUS_PERMISSIONS, HEATMAP_PERMISSION_KEYS, type PermissionKey } from "@cloud-checker/shared";
import { escapeCsvField } from "../ui/csv";
import type { HeatmapCell, HeatmapRow } from "../ui/heatmap";
import type { PermissionMapUserDto, PermissionSetSummaryDto } from "./permission-map-api";

export interface PermissionMapFilter {
  readonly search?: string;
  // 空文字は「プロファイル名を読めなかったユーザー」という実在の値。未指定は必ずキーの省略で
  // 表す（exactOptionalPropertyTypes なので undefined を渡す必要もない）。
  readonly profile?: string;
  readonly permissionSet?: string;
  // ヒートマップのセル押下（行=プロファイル × 列=危険権限）で使う。綴り違いを型で弾く。
  readonly permission?: PermissionKey;
  readonly dangerOnly?: boolean;
}

const LABEL_BY_KEY = new Map<PermissionKey, string>(DANGEROUS_PERMISSIONS.map((p) => [p.key, p.label] as const));

// 「重大/高」の唯一の定義。severity（wire の値）で数え直してはいけない: この集合はヒートマップの
// 列そのもので、列・バッジ・絞り込み・並び順が別々の判定を持つと、API と SPA の版ずれ
// （permission-map-api.ts が前提として明記している状態）でセルが「保有者 1 名」と主張する人を
// 「重大/高の権限保有のみ」表示が消し、並び順も最下段へ落とす。
const HEATMAP_KEYS: ReadonlySet<PermissionKey> = new Set(HEATMAP_PERMISSION_KEYS);

// ヒートマップの列（critical + high の 7 種）の表示名。行によってはセルを出さない
// （下記 buildHeatmapRows）ため、createHeatmap には必ずこの列リストを渡して列の同一性を固定する。
export const HEATMAP_COLUMN_LABELS: readonly string[] = HEATMAP_PERMISSION_KEYS.map(
  (key) => LABEL_BY_KEY.get(key) ?? key,
);

// ヒートマップのセル値は人数。createHeatmap は単位をドメイン側に委ねる（既定は素の数値）ため、
// これを渡さないとセルの読み上げ名が「システム管理者 全データの参照 2」になる。
export const formatPermissionHolderCount = (value: number): string => `${value}名`;

// dangerCountOf が数えるのは critical/high（＝ヒートマップの列）だけで、medium（IP アドレスの
// 管理）/ low（API の有効化）は 0 と数えられる。UI 文言を「危険権限」と書くと、それらだけを
// 持つユーザーが「危険権限 0 件」・チェックボックスで非表示になり「危険権限なし」と読めてしまう。
// 画面はこの定数を使い、数えている範囲（重大/高）をそのまま名乗る。
export const DANGER_COUNT_LABEL = "重大/高の権限数";
export const DANGER_ONLY_LABEL = "重大/高の権限保有のみ";

// PermissionSetSummaryDto.holderCount の見出し。割当行から数えるため無効化済みユーザーや期限切れ
// 割当を含み、意図的に過大側に出る（permission-map-api.ts の契約）。「保有者数」と書くと正確な
// 人数に見えるため、画面はこの文言で「何を数えたか」を名乗る。
export const HOLDER_COUNT_LABEL = "割当数（無効化・期限切れを含む）";

// プロファイル名が空（backend の str(Profile.Name) が読めなかった場合）の行の表示名。
// "" を行見出しにすると th が無ラベルになり、どの母集団かも読み取れない。
export const UNKNOWN_PROFILE_LABEL = "プロファイル不明";

// 重大/高の「種類数」。同じ権限を複数経路で持っても 1 と数える（人の多重計上を避ける）。
export function dangerCountOf(user: PermissionMapUserDto): number {
  const keys = new Set(user.grants.filter((g) => HEATMAP_KEYS.has(g.permission)).map((g) => g.permission));
  return keys.size;
}

// サマリ帯の「権限を確定できない N 名」。0 名として畳み込まれる母集団を必ず数値で出すため。
export function countUnresolved(users: readonly PermissionMapUserDto[]): number {
  return users.filter((u) => u.hasUnresolvedAssignment).length;
}

/**
 * 絞り込み後の一覧ではなく、その**母集団**の未確定人数を数える（`permission` 句を外した結果）。
 *
 * 未確定ユーザーは「その権限を持つか」を評価できないため `permission` で絞ると必ず落ちる。
 * ヒートマップのセル押下（プロファイル × 権限）後に絞り込み結果だけを `countUnresolved` すると、
 * 母集団に未確定が居ても「権限を確定できない 0 名」と書けてしまう（偽のゼロ）。
 * 画面はセル押下時にこちらを使い、「確定できた保有者 N 名 / 母集団の未確定 M 名」を並べて出す。
 */
export function countUnresolvedInPopulation(
  users: readonly PermissionMapUserDto[],
  filter: PermissionMapFilter,
): number {
  const { permission: _permission, ...population } = filter;
  return countUnresolved(filterUsers(users, population));
}

export function sortUsersByRisk(users: readonly PermissionMapUserDto[]): PermissionMapUserDto[] {
  // 未確定ユーザーは確定した危険権限が 0 件なので、数だけで並べると本当に権限が無い人の中に
  // 埋もれて最下段に落ちる（数百人の既定表示では画面外）。同数なら未確定を先に出す。
  const unresolvedRank = (u: PermissionMapUserDto): number => (u.hasUnresolvedAssignment ? 1 : 0);
  return [...users].sort(
    (a, b) =>
      dangerCountOf(b) - dangerCountOf(a) ||
      unresolvedRank(b) - unresolvedRank(a) ||
      a.name.localeCompare(b.name, "ja"),
  );
}

export function filterUsers(users: readonly PermissionMapUserDto[], filter: PermissionMapFilter): PermissionMapUserDto[] {
  const q = (filter.search ?? "").trim().toLowerCase();
  return users.filter((u) => {
    if (q !== "" && !(u.name.toLowerCase().includes(q) || u.username.toLowerCase().includes(q))) return false;
    // 空文字を「未指定」と解釈してはいけない: backend は読めなかった Profile.Name / PermissionSet.Label を
    // "" に落とすため、"" は実在の母集団（かつ hasUnresolvedAssignment の中心）を指す。センチネル扱いすると
    // ヒートマップの「プロファイル不明」セルを押した結果が全プロファイルのユーザー一覧になり、
    // セルが主張した人数と中身が食い違う。
    if (filter.profile !== undefined && u.profile !== filter.profile) return false;
    if (filter.permissionSet !== undefined && !u.permissionSetLabels.includes(filter.permissionSet)) return false;
    if (filter.permission !== undefined && !u.grants.some((g) => g.permission === filter.permission)) return false;
    // 危険のみ: 実効権限を確定できなかったユーザーは残す。落とすと「危険権限を持つ人ではない」と
    // 断定したことになり、偽の合格を出す（spec の正直さ要件）。
    if (filter.dangerOnly === true && dangerCountOf(u) === 0 && !u.hasUnresolvedAssignment) return false;
    return true;
  });
}

export interface HeatmapProfileRow extends HeatmapRow {
  /** 絞り込みに渡す実際の profile 値（空プロファイル行は ""）。`row` は表示専用のラベル。 */
  readonly profile: string;
  /** 行の総人数（確定 + 未確定）。 */
  readonly userCount: number;
  /** 行のうち実効権限を確定できなかった人数。画面はこれを注記する。 */
  readonly unresolvedCount: number;
}

// 空プロファイル行の表示名を決める。実在プロファイルが同名だと 2 つの母集団が 1 行に見え、
// セル押下の逆引き（profileOfHeatmapRow）も取り違えるため、衝突したら必ずずらす。
function buildProfileLabeler(profiles: readonly string[]): (profile: string) => string {
  if (!profiles.includes("")) return (profile) => profile;
  const taken = new Set(profiles.filter((profile) => profile !== ""));
  let label = UNKNOWN_PROFILE_LABEL;
  for (let n = 2; taken.has(label); n += 1) label = `${UNKNOWN_PROFILE_LABEL} (${n})`;
  return (profile) => (profile === "" ? label : profile);
}

/**
 * 行=プロファイル × 列=危険権限（critical+high）の人数。
 *
 * 確定できた保有者だけを数えるため、**その行に確定情報が 1 件も無い場合はセルを出さない**
 * （`cells: []`）。0 を並べると「このプロファイルには重大/高の権限を持つ人がいない」と読めるが、
 * 実際は誰の権限も確定できていない状態だから。createHeatmap 側は欠けた列を「データなし」と
 * 描き、セルもボタンにしない（＝押せない）。
 *
 * 一部だけ未確定の行は人数を出す（確定分は事実なので隠さない）。ただしその数は下限であって
 * 全数ではないため、`unresolvedCount` を必ず画面に出して補う。
 */
export function buildHeatmapRows(users: readonly PermissionMapUserDto[]): HeatmapProfileRow[] {
  // プロファイルごとに 1 回だけ走査する（プロファイル数 × 列数ぶん再走査しない）。
  const byProfile = new Map<string, PermissionMapUserDto[]>();
  for (const user of users) {
    const group = byProfile.get(user.profile);
    if (group === undefined) byProfile.set(user.profile, [user]);
    else group.push(user);
  }
  const labelOf = buildProfileLabeler([...byProfile.keys()]);
  return [...byProfile.entries()]
    .map(([profile, rowUsers]): HeatmapProfileRow => {
      let unresolvedCount = 0;
      const holders = new Map<PermissionKey, number>();
      for (const user of rowUsers) {
        if (user.hasUnresolvedAssignment) unresolvedCount += 1;
        // 同じ権限を複数経路で持つ人を 2 名と数えない（セルは人数）。
        const counted = new Set<PermissionKey>();
        for (const g of user.grants) {
          if (!HEATMAP_KEYS.has(g.permission) || counted.has(g.permission)) continue;
          counted.add(g.permission);
          holders.set(g.permission, (holders.get(g.permission) ?? 0) + 1);
        }
      }
      const cells: HeatmapCell[] =
        unresolvedCount === rowUsers.length
          ? []
          : HEATMAP_PERMISSION_KEYS.map((key, i) => ({
              column: HEATMAP_COLUMN_LABELS[i] ?? key,
              value: holders.get(key) ?? 0,
            }));
      return { row: labelOf(profile), profile, userCount: rowUsers.length, unresolvedCount, cells };
    })
    // 行順は表示ラベルの昇順で固定する（入力順のままだと再描画のたびに行が入れ替わる）。
    .sort((a, b) => a.row.localeCompare(b.row, "ja"));
}

/**
 * ヒートマップのセル押下から絞り込み用の profile 値を引く（渡ってくるのは表示ラベル）。
 * 表示ラベルをそのまま `filterUsers` に渡すと、空プロファイル行では 0 件になり、
 * ラベル衝突時は別の母集団を混ぜる。該当行が無ければ `undefined`（絞り込みを変えない）。
 */
export function profileOfHeatmapRow(
  rows: readonly HeatmapProfileRow[],
  rowLabel: string,
): string | undefined {
  return rows.find((r) => r.row === rowLabel)?.profile;
}

/**
 * 逆方向（profile 値 → 行と同じ表示ラベル）。ユーザー表のプロファイル列はこれを使う。
 * 画面側で `profile === "" ? UNKNOWN_PROFILE_LABEL : profile` を書き直すと、実在プロファイルが
 * 「プロファイル不明」という名前のときにヒートマップ（「プロファイル不明 (2)」）と食い違い、
 * 表では 2 つの母集団が同じ文字列になる。行が無い場合も "" を無ラベルで返さない。
 */
export function profileLabelOf(rows: readonly HeatmapProfileRow[], profile: string): string {
  const row = rows.find((r) => r.profile === profile);
  if (row !== undefined) return row.row;
  return profile === "" ? UNKNOWN_PROFILE_LABEL : profile;
}

export function unusedPermissionSets(sets: readonly PermissionSetSummaryDto[]): PermissionSetSummaryDto[] {
  return sets.filter((s) => !s.isOwnedByProfile && s.holderCount === 0);
}

export function topPermissionSets(sets: readonly PermissionSetSummaryDto[], limit: number): PermissionSetSummaryDto[] {
  // filter が新しい配列を返すので、その場で sort してよい（入力は変更しない）。
  return sets.filter((s) => !s.isOwnedByProfile).sort((a, b) => b.holderCount - a.holderCount).slice(0, limit);
}

// 生キー列（permission / severity）と表示名列（permissionLabel）を併記する。ui/checksheet の
// CSV と同じ流儀: ラベルだけだと表示名の変更で run 間の差分・grep が壊れ、キーだけだと
// 表計算でそのまま読めない。
const CSV_HEADER = [
  "name",
  "username",
  "profile",
  "permission",
  "permissionLabel",
  "severity",
  "source",
  "sourceLabel",
  "unresolved",
];

export function permissionMapToCsv(users: readonly PermissionMapUserDto[]): string {
  const lines: string[] = [];
  for (const u of users) {
    // 未確定フラグは全行に出す。監査担当者が CSV を絞り込んだ結果から「確定済みで権限なし」と
    // 読めてしまわないため（画面と同じ正直さを書き出し側でも保つ）。
    const unresolved = String(u.hasUnresolvedAssignment);
    if (u.grants.length === 0) {
      // 重大/高の権限が無いユーザーも 1 行出す（一覧から人が消えると棚卸しにならない）。
      lines.push([u.name, u.username, u.profile, "", "", "", "", "", unresolved].map(escapeCsvField).join(","));
      continue;
    }
    for (const g of u.grants) {
      lines.push(
        [u.name, u.username, u.profile, g.permission, g.label, g.severity, g.source, g.sourceLabel, unresolved]
          .map(escapeCsvField)
          .join(","),
      );
    }
  }
  return [CSV_HEADER.join(","), ...lines].join("\n") + "\n";
}
